import { AppKitNetwork } from '@reown/appkit/networks';
import {
  LuArrowDownToLine,
  LuArrowUpFromLine,
  LuRoute,
  LuLock,
} from 'react-icons/lu';
import { BsPatchPlus } from 'react-icons/bs';
import { HeaderLink } from 'src/types';
import { FaCoins } from 'react-icons/fa';

export const SHOW_SWAPR_PAGE = import.meta.env.VITE_SHOW_SWAPR_PAGE === 'true';
export const SHOW_DEFI_PAGE = import.meta.env.VITE_SHOW_DEFI_PAGE === 'true';

export const SHOW_GAS_FEES = import.meta.env.VITE_SHOW_GAS_FEES === 'true';

export const DOCS_LINK = import.meta.env.VITE_DOCS_LINK;

export const DISCORD_LINK =
  import.meta.env.VITE_DISCORD_LINK || 'https://discord.gg/capricorn';

export const ALWAYS_SHOW_STAKING_STATUS_BANNER =
  import.meta.env.VITE_ALWAYS_SHOW_STAKING_STATUS_BANNER === 'true';

export const STATUSPAGE_LINK =
  import.meta.env.VITE_STATUSPAGE_LINK || 'https://apriori.statuspage.io';

export const RECOMMEND_HAHA_WALLET =
  import.meta.env.VITE_RECOMMEND_HAHA_WALLET === 'true';

const MONAD_TESTNET_CHAIN_ID = Number(
  import.meta.env.VITE_MONAD_TESTNET_CHAIN_ID || 10143
);
const MONAD_MAINNET_CHAIN_ID = Number(
  import.meta.env.VITE_MONAD_MAINNET_CHAIN_ID || 143
);

export const CHAIN_ID = Number(
  import.meta.env.VITE_CHAIN_ID || MONAD_TESTNET_CHAIN_ID
);
export const INDEXER_API_URL =
  import.meta.env.VITE_INDEXER_API_URL ||
  'https://liquid-staking-backend-cf4eed5fabe6.herokuapp.com';
export const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID;

const STAKING_CONTRACT_ADDRESS = import.meta.env.VITE_STAKING_CONTRACT_ADDRESS;

let _WORKERS_MAPPING;

try {
  _WORKERS_MAPPING = JSON.parse(import.meta.env.VITE_WORKERS_MAPPING || '{}');
} catch (error) {
  _WORKERS_MAPPING = {};
}

export const WORKERS_MAPPING = _WORKERS_MAPPING;

type ChainAddresses = {
  staking: `0x${string}`;
  nns?: `0x${string}`;
};

const CONTRACT_ADDRESSES_MAPPING: Record<number, ChainAddresses> = {
  [MONAD_TESTNET_CHAIN_ID]: {
    // Testnet Monad
    staking: STAKING_CONTRACT_ADDRESS,
  },
  [MONAD_MAINNET_CHAIN_ID]: {
    // Monad Mainnet
    staking: STAKING_CONTRACT_ADDRESS,
  },
};

export const CONTRACT_ADDRESSES = CONTRACT_ADDRESSES_MAPPING[CHAIN_ID];

type NetworkVars = {
  explorer: string;
  tokensAddresses: { MON: `0x${string}`; APRMON: `0x${string}` };
};

const chainPublicUrls: Record<number, string> = {
  // [MONAD_TESTNET_CHAIN_ID]: '',
};

const getRpcUrls = (chainId: number) => {
  const publicUrl = chainPublicUrls[chainId];

  return ((import.meta.env.VITE_RPC_URLS as string) || '')
    .split('_._')
    .map((url) => (url === 'PUBLIC' ? publicUrl : url))
    .filter(Boolean);
};

const NETWORK_VARS_MAPPING: Record<number, NetworkVars> = {
  [MONAD_TESTNET_CHAIN_ID]: {
    explorer: 'https://testnet.monadexplorer.com',
    tokensAddresses: {
      MON: '0x0000000000000000000000000000000000000000',
      APRMON: '0xb2f82D0f38dc453D596Ad40A37799446Cc89274A',
    },
  },
  [MONAD_MAINNET_CHAIN_ID]: {
    explorer: 'https://monadvision.com/',
    tokensAddresses: {
      MON: '0x0000000000000000000000000000000000000000',
      APRMON: '0x0c65a0bc65a5d819235b71f554d210d3f80e0852',
    },
  },
};

const NETWORKS_MAPPING: Record<number, AppKitNetwork> = {
  [MONAD_TESTNET_CHAIN_ID]: {
    id: MONAD_TESTNET_CHAIN_ID,
    name: 'Monad Testnet',
    nativeCurrency: {
      name: 'Monad',
      symbol: 'MON',
      decimals: 18,
    },
    rpcUrls: {
      default: {
        http: getRpcUrls(MONAD_TESTNET_CHAIN_ID),
      },
    },
    blockExplorers: {
      default: {
        name: 'Monad Explorer',
        url: NETWORK_VARS_MAPPING[MONAD_TESTNET_CHAIN_ID].explorer,
        apiUrl: `${NETWORK_VARS_MAPPING[MONAD_TESTNET_CHAIN_ID].explorer}/api`,
      },
    },
  },
  [MONAD_MAINNET_CHAIN_ID]: {
    id: MONAD_MAINNET_CHAIN_ID,
    name: 'Monad Mainnet',
    nativeCurrency: {
      name: 'Monad',
      symbol: 'MON',
      decimals: 18,
    },
    rpcUrls: {
      default: {
        http: getRpcUrls(MONAD_MAINNET_CHAIN_ID),
      },
    },
    blockExplorers: {
      default: {
        name: 'MonVision',
        url: NETWORK_VARS_MAPPING[MONAD_MAINNET_CHAIN_ID].explorer,
        apiUrl: `${NETWORK_VARS_MAPPING[MONAD_MAINNET_CHAIN_ID].explorer}/api`,
      },
    },
  },
};

export const NETWORK_VARS = NETWORK_VARS_MAPPING[CHAIN_ID];

export const RPC_URLS = getRpcUrls(CHAIN_ID);

export const NETWORK_INFO = NETWORKS_MAPPING[CHAIN_ID];

export const MAX_DECIMALS_DISPLAY = 4;

export const MAX_SAFE_DEADLINE = 340282366920938463463374607431768211455n;

export const HEADER_LINKS: HeaderLink[] = [
  {
    to: '/',
    label: 'Stake',
    subLinks: [
      {
        to: '/',
        label: 'Stake',
        icon: <LuArrowDownToLine />,
        usePreciseSearchParamsMatch: true,
      },
      {
        to: '/?tab=unstake',
        label: 'Unstake',
        icon: <LuArrowUpFromLine />,
        usePreciseSearchParamsMatch: true,
      },
      {
        to: '/?tab=unstake-claim',
        label: 'Claim',
        icon: <BsPatchPlus />,
        usePreciseSearchParamsMatch: true,
      },
    ],
  },
  ...(SHOW_SWAPR_PAGE ? [{ to: '/swapr', label: 'Swapr' }] : []),
  ...(SHOW_DEFI_PAGE ? [{ to: '/ecosystem', label: 'Ecosystem' }] : []),
  {
    to: 'https://participate.capricorn.tech',
    label: 'Participate',
    subLinks: [
      {
        to: 'https://participate.capricorn.tech',
        label: 'Stake',
        icon: <FaCoins size={24} color="currentColor" />,
      },
      {
        to: 'https://stargate.finance/bridge?srcChain=ethereum&srcToken=0x5A9610919f5e81183823A2be4Bd1BeB2B4da2a20&dstChain=bsc&dstToken=0x299AD4299Da5b2b93Fba4c96967B040C7F611099',
        label: 'Bridge',
        icon: <LuRoute size={24} color="currentColor" />,
      },
      {
        to: 'https://participate.capricorn.tech/lock',
        label: 'Lock',
        icon: <LuLock size={24} color="currentColor" />,
      },
    ],
  },
];
